import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../lib/api";
import { downloadBlob } from "../lib/download";
import { toast } from "sonner";
import { Download, FileText, X } from "lucide-react";
import { fmtD } from "./ClientAccount";

const fieldCls = "rounded-lg px-2.5 py-1.5 outline-none border focus:ring-2 focus:ring-[#F97316] text-xs w-full";
const border = { borderColor: "#E5E7EB" };

const blobError = async (e) => {
  try { return JSON.parse(await e.response.data.text()).detail; } catch { return "Generation failed"; }
};

const fnameFrom = (headers, fallback) => {
  const m = (headers?.["content-disposition"] || "").match(/filename="([^"]+)"/);
  return m ? m[1] : fallback;
};

function Modal({ open, onClose, title, testId, children }) {
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: "rgba(10,31,68,0.45)" }} data-testid={testId}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-base" style={{ color: "#0A1F44" }}>{title}</h3>
          <button data-testid={`${testId}-close`} onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-[#6B7280]"><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
}

const BIZ_FIELDS = [
  ["abn", "ABN"],
  ["account_name", "Account name"],
  ["bsb", "BSB"],
  ["account_number", "Account number"],
];

export function BusinessDetailsModal({ open, onClose }) {
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (open) api.get("/admin/business-settings").then(({ data }) => setForm(data));
  }, [open]);

  const save = async () => {
    await api.put("/admin/business-settings", form);
    toast.success("Business details saved");
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title="Business details" testId="business-details-modal">
      {form && (
        <div className="space-y-3">
          <p className="text-xs text-[#6B7280]">Shown on generated statements and invoices. Payment details appear on invoices only.</p>
          {BIZ_FIELDS.map(([key, label]) => (
            <div key={key}>
              <label className="text-xs text-[#6B7280] mb-1 block">{label}</label>
              <input data-testid={`bd-${key}`} value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className={fieldCls} style={border} />
            </div>
          ))}
          <label className="flex items-center gap-2 text-xs font-medium cursor-pointer" style={{ color: "#0A1F44" }}>
            <input data-testid="bd-gst" type="checkbox" checked={!!form.gst_registered} onChange={(e) => setForm({ ...form, gst_registered: e.target.checked })} className="h-4 w-4 accent-[#F97316]" />
            Registered for GST (new documents default to including 10% GST)
          </label>
          <button data-testid="bd-save" onClick={save} className="w-full rounded-lg px-4 py-2 font-semibold text-white text-sm" style={{ backgroundColor: "#0A1F44" }}>Save</button>
        </div>
      )}
    </Modal>
  );
}

function TypePicker({ type, setType }) {
  const opt = (val, label) => (
    <button data-testid={`gen-type-${val}`} onClick={() => setType(val)}
      className="flex-1 rounded-lg px-3 py-2 text-xs font-bold border transition-colors"
      style={type === val ? { backgroundColor: "#0A1F44", color: "#fff", borderColor: "#0A1F44" } : { color: "#6B7280", borderColor: "#E5E7EB" }}>
      {label}
    </button>
  );
  return <div className="flex gap-2">{opt("statement", "Statement")}{opt("invoice", "Invoice")}</div>;
}

function GenerateNotes({ type, unpriced }) {
  if (type === "invoice" && unpriced.length > 0) {
    return (
      <p data-testid="gen-unpriced-warning" className="text-xs rounded-lg px-3 py-2" style={{ backgroundColor: "#FEE2E2", color: "#B91C1C" }}>
        These items have no price yet and can't be invoiced: {unpriced.map((r) => r.item).join(", ")}. Deselect them or choose Statement (they'll show as TBC).
      </p>
    );
  }
  if (type === "invoice") {
    return (
      <p className="text-xs rounded-lg px-3 py-2" style={{ backgroundColor: "#FEF0E6", color: "#B45309" }}>
        Generating an invoice assigns the next invoice number, sets today as the issue date (payment due in 14 days) and marks the selected rows as <b>Invoiced</b>.
      </p>
    );
  }
  return null;
}

async function generateBillingDoc(clientId, payload) {
  const res = await api.post(`/clients/${clientId}/billing-docs`, payload, { responseType: "blob" });
  downloadBlob(res.data, fnameFrom(res.headers, `${payload.type}.pdf`),
    payload.type === "invoice" ? "Invoice generated" : "Statement generated");
}

export function GenerateDocDialog({ open, onClose, clientId, rows, selectedIds, onDone }) {
  const [type, setType] = useState("statement");
  const [gst, setGst] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) api.get("/admin/business-settings").then(({ data }) => setGst(!!data.gst_registered));
  }, [open]);

  const unpriced = rows.filter((r) => selectedIds.includes(r.id) && r.amount == null);
  const blocked = type === "invoice" && unpriced.length > 0;

  const generate = async () => {
    setBusy(true);
    try {
      await generateBillingDoc(clientId, { type, gst, row_ids: selectedIds });
      onDone();
      onClose();
    } catch (e) {
      toast.error(await blobError(e));
    }
    setBusy(false);
  };

  return (
    <Modal open={open} onClose={onClose} title={`Generate document — ${selectedIds.length} item${selectedIds.length === 1 ? "" : "s"}`} testId="gen-doc-dialog">
      <div className="space-y-4">
        <TypePicker type={type} setType={setType} />
        <label className="flex items-center gap-2 text-xs font-medium cursor-pointer" style={{ color: "#0A1F44" }}>
          <input data-testid="gen-gst-toggle" type="checkbox" checked={gst} onChange={(e) => setGst(e.target.checked)} className="h-4 w-4 accent-[#F97316]" />
          Include GST (10% added on top{type === "invoice" ? " — header reads TAX INVOICE" : ""})
        </label>
        <GenerateNotes type={type} unpriced={unpriced} />
        <button data-testid="gen-doc-confirm" disabled={busy || blocked || !selectedIds.length} onClick={generate}
          className="w-full flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 font-bold text-white text-sm disabled:opacity-40" style={{ backgroundColor: "#F97316" }}>
          <FileText size={15} /> {busy ? "Generating..." : `Generate ${type === "invoice" ? (gst ? "tax invoice" : "invoice") : "statement"} PDF`}
        </button>
      </div>
    </Modal>
  );
}

export function BillingDocsHistory({ clientId, refreshKey }) {
  const [docs, setDocs] = useState([]);

  useEffect(() => {
    if (clientId) api.get(`/clients/${clientId}/billing-docs`).then(({ data }) => setDocs(data));
  }, [clientId, refreshKey]);

  const download = async (d) => {
    try {
      const res = await api.get(`/billing-docs/${d.id}/pdf`, { responseType: "blob" });
      downloadBlob(res.data, `${d.ref}.pdf`);
    } catch {
      toast.error("Download failed");
    }
  };

  if (!docs.length) return null;
  return (
    <div className="mt-6" data-testid="billing-docs-history">
      <h3 className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: "#6B7280" }}>Generated documents</h3>
      <div className="rounded-xl border bg-white divide-y" style={border}>
        {docs.map((d) => (
          <div key={d.id} data-testid={`billing-doc-item-${d.id}`} className="flex items-center justify-between px-4 py-2.5">
            <div className="flex items-center gap-3">
              <span className="text-xs font-bold" style={{ color: "#0A1F44" }}>{d.ref}</span>
              <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded" style={{ backgroundColor: d.type === "invoice" ? "#FEF0E6" : "#E8EDF4", color: d.type === "invoice" ? "#F97316" : "#0A1F44" }}>
                {d.type}{d.gst ? " · GST" : ""}
              </span>
              <span className="text-xs text-[#9CA3AF]">{d.items} item{d.items === 1 ? "" : "s"} · {fmtD(d.created_at)}</span>
            </div>
            <button data-testid={`billing-doc-download-${d.id}`} onClick={() => download(d)} className="flex items-center gap-1 text-xs font-semibold hover:underline" style={{ color: "#F97316" }}>
              <Download size={13} /> Download
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
